<script setup lang="ts">
/**
 * 智能客服 —— 居中悬浮聊天窗,全局挂在 AppLayout(任意页可唤起)。
 * 无遮罩 → 后面的 App 照常可点击操作(边问边操作);标题栏可拖动、可最小化。
 * 默认居中,可拖到任意位置;max-height 留边,输入框不被 Windows 任务栏遮。
 *
 * 接入 iris(AI 助手 sidecar):只发用户最新一条,多轮历史由后端 /support/chat
 * 透传给 iris、按 session_id 加载。流式逐 token 渲染。知识(操作 FAQ)在 iris
 * 侧 persona 配置,这里不感知。
 */
import { computed, nextTick, ref, watch } from 'vue'
import { useSupport } from '@/composables/useSupport'
import { useAuthStore } from '@/stores/auth'
import { isTauri, getAiConfig } from '@/composables/useDesktopBridge'
import { isLocalDesktop } from '@/edition'
import { resetSupportSession, streamChat, supportSessionId } from '@/api/support'

interface ChatMsg {
  role: 'user' | 'assistant'
  content: string
}

const { open, closeSupport, showRequest } = useSupport()
const auth = useAuthStore()

const messages = ref<ChatMsg[]>([])
const input = ref('')
const sending = ref(false)
const scrollEl = ref<HTMLElement | null>(null)

// 悬浮窗:可拖动 + 可最小化,不带遮罩 → 后面的 App 照常可操作。
const panelEl = ref<HTMLElement | null>(null)
const minimized = ref(false)
// null = 默认锚定右下角;一旦拖动改用绝对 left/top。
const pos = ref<{ left: number; top: number } | null>(null)
const panelStyle = computed(() =>
  pos.value
    ? { left: `${pos.value.left}px`, top: `${pos.value.top}px`, right: 'auto', bottom: 'auto', margin: '0' }
    : {}
)
let dragging = false
let dragOrigin = { x: 0, y: 0, left: 0, top: 0 }

function onDragStart(e: PointerEvent) {
  // 标题栏上的按钮不触发拖动。
  if ((e.target as HTMLElement).closest('.icon-btn')) return
  const el = panelEl.value
  if (!el) return
  const r = el.getBoundingClientRect()
  dragging = true
  dragOrigin = { x: e.clientX, y: e.clientY, left: r.left, top: r.top }
  pos.value = { left: r.left, top: r.top }
  window.addEventListener('pointermove', onDragMove)
  window.addEventListener('pointerup', onDragEnd)
}
function onDragMove(e: PointerEvent) {
  if (!dragging) return
  const w = panelEl.value?.offsetWidth ?? 380
  const left = Math.min(
    Math.max(8, dragOrigin.left + (e.clientX - dragOrigin.x)),
    Math.max(8, window.innerWidth - w - 8)
  )
  const top = Math.min(
    Math.max(8, dragOrigin.top + (e.clientY - dragOrigin.y)),
    Math.max(8, window.innerHeight - 44) // 至少把标题栏留在屏内
  )
  pos.value = { left, top }
}
function onDragEnd() {
  dragging = false
  window.removeEventListener('pointermove', onDragMove)
  window.removeEventListener('pointerup', onDragEnd)
}

// 收起/展开。收起前把居中态固化为像素坐标 —— 否则 height:auto 下标题栏会跳到顶部。
function toggleMin() {
  if (!minimized.value && !pos.value && panelEl.value) {
    const r = panelEl.value.getBoundingClientRect()
    pos.value = { left: r.left, top: r.top }
  }
  minimized.value = !minimized.value
}

let sessionId = supportSessionId()
let controller: AbortController | null = null

// 桌面单机版智能客服走本机 iris,需先在「设置 → 套餐与 AI → 配置模型」配置大模型 key。
// 云版 / 浏览器走服务端平台 key,不拦。
const needsLocalAiKey = isTauri() && isLocalDesktop()
const aiKeyMissing = ref(false)
const NEED_KEY_MSG = '智能客服需先在 设置 → 套餐与 AI → 配置模型 后才能使用。'
async function refreshAiKeyStatus() {
  if (!needsLocalAiKey) {
    aiKeyMissing.value = false
    return
  }
  try {
    aiKeyMissing.value = !(await getAiConfig()).configured
  } catch {
    aiKeyMissing.value = false // 探测失败不误拦,让真实请求去报错
  }
}

// 空态示例问题(知识就绪后可换成真实高频问)。
const EXAMPLES = ['怎么批量上传图片?', '以图搜图怎么用?', '怎么开通小程序?']

function scrollToBottom() {
  void nextTick(() => {
    const el = scrollEl.value
    if (el) el.scrollTop = el.scrollHeight
  })
}

async function send(text?: string) {
  const content = (text ?? input.value).trim()
  if (!content || sending.value) return
  input.value = ''
  messages.value.push({ role: 'user', content })
  // 先 push 占位 AI 气泡,再取回**响应式**引用(经数组代理),逐 token 改它的
  // content 才能触发重渲染;持有原始对象直接改是不会触发的。
  messages.value.push({ role: 'assistant', content: '' })
  const assistant = messages.value[messages.value.length - 1]
  // 桌面端没配 key:直接给出引导,不发请求(否则必走「抱歉,出错了」错误路径)。
  if (aiKeyMissing.value) {
    assistant.content = NEED_KEY_MSG
    scrollToBottom()
    return
  }
  sending.value = true
  scrollToBottom()

  controller = new AbortController()
  try {
    await streamChat({
      message: content,
      sessionId,
      signal: controller.signal,
      onToken: (delta) => {
        assistant.content += delta
        scrollToBottom()
      }
    })
    if (!assistant.content) assistant.content = '(没有收到回复,请重试)'
  } catch (e) {
    const err = e as Error
    if (err?.name === 'AbortError') {
      // 用户主动中断:空泡就移除,有内容就保留已生成的部分。
      if (!assistant.content) messages.value.pop()
    } else if (needsLocalAiKey && aiKeyMissing.value) {
      // 兜底:并发探测把状态翻成「缺 key」时,给引导而非生硬报错。
      assistant.content = NEED_KEY_MSG
    } else {
      assistant.content = `抱歉,出错了:${err?.message ?? '请稍后重试'}`
    }
  } finally {
    sending.value = false
    controller = null
    scrollToBottom()
  }
}

function newChat() {
  controller?.abort()
  sessionId = resetSupportSession()
  messages.value = []
  input.value = ''
}

function onKeydown(e: KeyboardEvent) {
  // Enter 发送,Shift+Enter 换行。
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    void send()
  }
}

// 关窗时若仍在流式 → 中断(省 token + 防泄漏)。
watch(open, (v) => {
  if (!v) controller?.abort()
})

// 侧栏「智能客服」每次点击都自增 showRequest → 展开 + 居中,即便此刻已打开或已最小化
// (修「面板最小化后再点侧栏没反应」:那时 open 已是 true,不会触发上面的 watch)。
watch(showRequest, () => {
  minimized.value = false
  pos.value = null
  void refreshAiKeyStatus()
})

// 账号切换 / 登出 → 清空会话与界面、开新 session,避免共享电脑上聊天上下文(及界面里
// 的历史气泡)串到下一个用户。服务端按 user_id 命名空间化是硬隔离,这里是 UI 层兜底。
watch(
  () => auth.token,
  () => {
    newChat()
  }
)
</script>

<template>
  <transition name="support-pop">
    <aside
      v-if="open"
      ref="panelEl"
      class="support-panel"
      :class="{ 'is-min': minimized }"
      :style="panelStyle"
      role="dialog"
      aria-label="智能客服"
    >
      <header class="support-head" @pointerdown="onDragStart">
        <div class="support-title">
          <i class="ti ti-message-chatbot" aria-hidden="true"></i>
          <span>智能客服</span>
        </div>
        <div class="support-actions">
          <button v-if="!minimized" type="button" class="icon-btn" title="开新对话" @click="newChat">
            <i class="ti ti-refresh" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="icon-btn"
            :title="minimized ? '展开' : '最小化'"
            @click="toggleMin"
          >
            <i :class="['ti', minimized ? 'ti-arrows-diagonal' : 'ti-minus']" aria-hidden="true"></i>
          </button>
          <button type="button" class="icon-btn" title="关闭" @click="closeSupport">
            <i class="ti ti-x" aria-hidden="true"></i>
          </button>
        </div>
      </header>

      <template v-if="!minimized">
        <div ref="scrollEl" class="support-body">
            <!-- 空态:欢迎 + 示例问题 -->
            <div v-if="messages.length === 0" class="support-intro">
              <p class="intro-hi">你好 👋 我是智能客服，操作上的问题尽管问。</p>
              <div class="intro-examples">
                <button
                  v-for="ex in EXAMPLES"
                  :key="ex"
                  type="button"
                  class="example-chip"
                  @click="send(ex)"
                >
                  {{ ex }}
                </button>
              </div>
            </div>

            <div
              v-for="(m, i) in messages"
              :key="i"
              class="msg"
              :class="m.role === 'user' ? 'msg-user' : 'msg-ai'"
            >
              <div class="bubble">
                <span v-if="m.content">{{ m.content }}</span>
                <span v-else class="typing" aria-label="正在输入"><i></i><i></i><i></i></span>
              </div>
            </div>
          </div>

          <footer class="support-input">
            <textarea
              v-model="input"
              class="input-box"
              rows="1"
              placeholder="输入问题，Enter 发送 / Shift+Enter 换行"
              @keydown="onKeydown"
            ></textarea>
            <button
              type="button"
              class="send-btn"
              :disabled="sending || !input.trim()"
              :title="sending ? '回复中…' : '发送'"
              @click="send()"
            >
              <i :class="['ti', sending ? 'ti-loader-2 spin' : 'ti-send']" aria-hidden="true"></i>
            </button>
          </footer>
      </template>
    </aside>
  </transition>
</template>

<style scoped>
.support-panel {
  position: fixed;
  /* 默认居中:inset:0 + margin:auto(不用 transform,免与弹出过渡的 transform 打架)。
     拖动 / 最小化后由内联 left/top 接管定位。 */
  inset: 0;
  margin: auto;
  z-index: 300;
  width: 380px;
  max-width: calc(100vw - 32px);
  /* 固定高度但永不占满:max-height 留出上下边距,底部输入框始终高于任务栏。 */
  height: 560px;
  max-height: calc(100vh - 48px);
  background: var(--color-background-primary);
  border: 0.5px solid var(--color-border-tertiary);
  border-radius: var(--border-radius-lg);
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  /* 裁掉子元素四角,贴合圆角卡片 */
  overflow: hidden;
}
/* 最小化:只剩可拖动的标题栏 */
.support-panel.is-min {
  height: auto;
}

/* ── 头部 ── */
.support-head {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: var(--color-background-secondary);
  border-bottom: 0.5px solid var(--color-border-tertiary);
  cursor: move;
  user-select: none;
  touch-action: none;
}
.support-panel.is-min .support-head {
  border-bottom: none;
}
.support-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: var(--fs-item);
  font-weight: var(--fw-med);
  color: var(--color-text-primary);
}
.support-title i {
  font-size: 18px;
  color: var(--color-brand);
}
.support-actions {
  display: flex;
  gap: 2px;
}
.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: var(--border-radius-md);
  background: transparent;
  color: var(--color-text-tertiary);
  cursor: pointer;
  font-family: inherit;
}
.icon-btn:hover {
  background: var(--color-background-secondary);
  color: var(--color-text-primary);
}
.icon-btn i {
  font-size: 17px;
}

/* ── 消息区 ── */
.support-body {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.support-intro {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 4px;
}
.intro-hi {
  margin: 0;
  font-size: var(--fs-control);
  color: var(--color-text-secondary);
  line-height: 1.6;
}
.intro-examples {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
}
.example-chip {
  border: var(--border-width-control) solid var(--color-border-control);
  background: var(--color-background-secondary);
  color: var(--color-text-secondary);
  border-radius: var(--border-radius-pill);
  padding: 6px 12px;
  font: inherit;
  font-size: var(--fs-control);
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
}
.example-chip:hover {
  background: var(--color-background-info);
  color: var(--color-text-info);
}

.msg {
  display: flex;
  max-width: 100%;
}
.msg-user {
  justify-content: flex-end;
}
.msg-ai {
  justify-content: flex-start;
}
.bubble {
  max-width: 82%;
  padding: 8px 11px;
  border-radius: var(--border-radius-lg);
  font-size: var(--fs-control);
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}
.msg-user .bubble {
  background: var(--color-brand);
  color: #fff;
  border-bottom-right-radius: 4px;
}
.msg-ai .bubble {
  background: var(--color-background-secondary);
  color: var(--color-text-primary);
  border-bottom-left-radius: 4px;
}

/* "正在输入"三点 */
.typing {
  display: inline-flex;
  gap: 4px;
  align-items: center;
  padding: 2px 0;
}
.typing i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-text-tertiary);
  animation: typing-blink 1.2s infinite ease-in-out both;
}
.typing i:nth-child(2) {
  animation-delay: 0.2s;
}
.typing i:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes typing-blink {
  0%,
  80%,
  100% {
    opacity: 0.25;
  }
  40% {
    opacity: 1;
  }
}

/* ── 输入区 ── */
.support-input {
  flex-shrink: 0;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 10px 12px;
  border-top: 0.5px solid var(--color-border-tertiary);
}
.input-box {
  flex: 1;
  resize: none;
  max-height: 120px;
  min-height: 38px;
  padding: 9px 10px;
  border: var(--border-width-control) solid var(--color-border-control);
  border-radius: var(--border-radius-md);
  background: var(--color-background-secondary);
  color: var(--color-text-primary);
  font: inherit;
  font-size: var(--fs-control);
  line-height: 1.4;
  outline: none;
}
.input-box:focus {
  border-color: var(--color-brand);
}
.send-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: none;
  border-radius: var(--border-radius-md);
  background: var(--color-brand);
  color: #fff;
  cursor: pointer;
  font-family: inherit;
}
.send-btn:hover:not(:disabled) {
  filter: brightness(1.06);
}
.send-btn:disabled {
  /* 待输入态:保留品牌色、只稍微淡一点点,不要灰扑扑没精神。 */
  opacity: 0.85;
  cursor: default;
}
.send-btn i {
  font-size: 17px;
}
.spin {
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── 过渡:从右下角轻轻弹出 ── */
.support-pop-enter-active,
.support-pop-leave-active {
  transition: transform 0.2s cubic-bezier(0.22, 0.61, 0.36, 1), opacity 0.2s ease;
}
.support-pop-enter-from,
.support-pop-leave-to {
  transform: translateY(12px) scale(0.98);
  opacity: 0;
}
</style>
